import React, { useState, useCallback, useEffect } from "react";

import { webLightTheme, webDarkTheme } from "@fluentui/react-components";
import { AppFrameProvider } from "@coherence-design-system/app-frame";
import {
    CoherenceTheme,
    CoherenceV9LightTheme,
    CoherenceV9DarkTheme,
} from "@coherence-design-system/styles";
import { useMsal, useAccount } from "@azure/msal-react";
import { useAppDispatch, useAppSelector } from "../hooks/redux";
import { AccountInfo } from "@azure/msal-browser";

import AppNavigation from "./nav";
import AppHeader from "../components/header";
import Footer from "../components/footer";
import { authActions } from "../store/reducers/authSlice";
import { checkForPermissionChange } from "../utils/checkForPermission";
import { getHomeSGNamesList } from "../store/actions/homeActions";
import { Stack, StackItem } from "@fluentui/react";
import NoPermission from "../pages/access-denied/noPermission";

const availableThemes = {
    default: { fluentV8: CoherenceTheme },
    light: { fluentV8: CoherenceV9LightTheme, fluent2: webLightTheme },
    dark: { fluentV8: CoherenceV9DarkTheme, fluent2: webDarkTheme },
    lightNeutral: {
        fluentV8: { ...CoherenceV9LightTheme, neutralHeaderEnabled: true },
        fluent2: webLightTheme,
    },
};

const Layout = (props: ILayoutProps) => {
    const { accounts } = useMsal();
    const account: AccountInfo | null = useAccount(accounts[0]);
    const dispatch = useAppDispatch();
    const [isNavMenuExpended, setIsNavMenuExpended] = useState(true);
    const sgNamesData: string | null = useAppSelector(
        (state) => state.home.sgName
    );
    const [sgNames, setSGNames] = useState<string | null>("");
    const [hasZeroPermission, setHasZeroPermission] = useState<boolean>(false);

// Get theme from Redux store
  const currentTheme = useAppSelector((state) => state.theme.theme);

  const initialDataSetup = useCallback(async () => {
    if (account) {
      const permissions: Array<string> =
        account?.idTokenClaims && account?.idTokenClaims["roles"]
          ? account?.idTokenClaims["roles"]
          : [];
      if (permissions.length <= 0) {
        setHasZeroPermission(true);
      }
      const userDetails = {
        name: account.name,
        userName: account.username,
        permissionRoles: permissions,
        sgNames: sgNames && sgNames.split(",").map((item) => item.trim()),
      };
      checkForPermissionChange(permissions);
      dispatch(
        authActions.fetchDataSuccess({
          userDetails: userDetails,
        })
      );
    }
  }, [account, dispatch, sgNames]);

    useEffect(() => {
        initialDataSetup();
    }, [initialDataSetup, account, dispatch]);

    useEffect(() => {
        if (!sgNamesData) {
            dispatch(getHomeSGNamesList());
        } else {
            setSGNames(sgNamesData);
        }
    }, [dispatch, sgNamesData]);

  const navMenuCallback = (isOpen: boolean) => {
    setIsNavMenuExpended(isOpen);
  };

// Apply theme class to the root element for CSS-based theming
  useEffect(() => {
    const rootElement = document.documentElement;
    const bodyElement = document.body;
    
    // Remove existing theme classes from all relevant elements
    rootElement.classList.remove('theme-default', 'theme-light', 'theme-dark', 'theme-lightNeutral');
    bodyElement.classList.remove('theme-default', 'theme-light', 'theme-dark', 'theme-lightNeutral');
    
    // Add new theme class to both root and body elements
    rootElement.classList.add(`theme-${currentTheme}`);
    bodyElement.classList.add(`theme-${currentTheme}`);
    
    // Apply theme to the app container as well
    const appContainer = document.getElementById('demo_app_frame');
    if (appContainer) {
      appContainer.className = `App-container theme-${currentTheme}`;
    }
        
    // Store the theme in localStorage for persistence between page loads
    localStorage.setItem('theme', currentTheme);
    
    // Add a small timeout to ensure the theme is fully applied after React rendering
    setTimeout(() => {
      // Force a repaint to ensure the theme styles are applied
      document.body.style.display = 'none';
      // document.body.offsetHeight; // Trigger a reflow
      void document.body.offsetHeight; // Trigger a reflow
      document.body.style.display = '';
      
      // Apply the theme to any iframes if present
      const iframes = document.querySelectorAll('iframe');
      iframes.forEach(iframe => {
        try {
          const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
          if (iframeDoc) {
            iframeDoc.documentElement.classList.remove('theme-default', 'theme-light', 'theme-dark', 'theme-lightNeutral');
            iframeDoc.documentElement.classList.add(`theme-${currentTheme}`);
            iframeDoc.body.classList.remove('theme-default', 'theme-light', 'theme-dark', 'theme-lightNeutral');
            iframeDoc.body.classList.add(`theme-${currentTheme}`);
          }
        } catch (e) {
          // Silently fail on cross-origin iframe access
        }
      });
    }, 50);
  }, [currentTheme]);

  return (
    <Stack>
      <StackItem>
        <AppFrameProvider
          availableThemes={availableThemes}
          defaultThemeKey={currentTheme}
        >
          <div slot="header">
            <AppHeader navMenuCallback={navMenuCallback} />
          </div>
          <div slot="navigation">
            <AppNavigation
              isNavMenuExpended={isNavMenuExpended}
              setIsNavMenuExpended={setIsNavMenuExpended}
            />
          </div>
          <div slot="main" style={{ height: "93%" }}>
            <main slot="mainContent">
              {hasZeroPermission && (
                <NoPermission
                  isModalOpen={hasZeroPermission}
                  onCancelClick={() => setHasZeroPermission(false)}
                />
              )}

                            {props.children}
                        </main>
                    </div>
                </AppFrameProvider>
            </StackItem>
            <StackItem>
                <Footer />
            </StackItem>
        </Stack>
    );
};

export default Layout;
